import { useState } from "react";
import axios from "axios";
import { Instagram, Facebook, Loader2, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SOCIALS = [
  { id: "instagram", icon: Instagram, label: "Instagram", href: "https://www.instagram.com/viralpulse.agency/" },
  { id: "tiktok", icon: TikTokIcon, label: "TikTok", href: "https://www.tiktok.com/@viralpulseagency" },
  { id: "facebook", icon: Facebook, label: "Facebook", href: "https://www.facebook.com/profile.php?id=61574871626723" },
];

const SERVICE_LINKS = [
  "Meta Ads", "Google Ads", "Lead Generation", "Websites",
  "Social Media", "Content & Creative", "Influencer Marketing",
];

const COMPANY_LINKS = [
  { id: "about", label: "About" },
  { id: "results", label: "Results" },
  { id: "contact", label: "Contact" },
];

const INPUT =
  "h-12 w-full rounded-xl border border-[#272732] bg-[#0B0B10] px-4 text-sm text-white placeholder:text-zinc-600 transition-colors focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500";

export const Footer = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      return setError("Please fill in all fields.");
    }
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer id="contact" className="border-t border-white/10 bg-[#030307]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 py-10 sm:px-8 sm:py-12 md:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <button
            type="button"
            data-testid="footer-logo-button"
            onClick={() => scrollToId("home")}
            aria-label="Viral Pulse Agency — back to top"
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 rounded-lg"
          >
            <img src="/assets/vp-logo.png" alt="Viral Pulse Agency" className="h-16 w-auto sm:h-20" />
          </button>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-zinc-400">
            Meta + Google ad campaigns that turn clicks into calls, leads and
            customers.
          </p>
          <p className="mt-4 text-sm font-medium text-zinc-500">
            <a href="mailto:Derek@ViralPulseAgency.com" data-testid="footer-email-link" className="transition-colors hover:text-purple-400">
              Derek@ViralPulseAgency.com
            </a>
          </p>
          <div className="mt-7 flex gap-3">
            {SOCIALS.map((s) => (
              <a
                key={s.id}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-${s.id}`}
                aria-label={s.label}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-zinc-400 transition-colors duration-300 hover:border-purple-500/60 hover:bg-purple-600 hover:text-white"
              >
                <s.icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Services</p>
          <ul className="mt-5 space-y-3">
            {SERVICE_LINKS.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  data-testid={`footer-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => scrollToId("services")}
                  className="text-left text-sm font-medium text-zinc-400 transition-colors hover:text-purple-400"
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Company</p>
          <ul className="mt-5 space-y-3">
            {COMPANY_LINKS.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  data-testid={`footer-company-${l.id}`}
                  onClick={() => scrollToId(l.id)}
                  className="text-sm font-medium text-zinc-400 transition-colors hover:text-purple-400"
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Contact Us</p>
          {sent ? (
            <div data-testid="contact-success" className="mt-5 flex items-start gap-3 rounded-xl border border-purple-500/30 bg-purple-600/10 p-5">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-purple-400" />
              <p className="text-sm font-medium text-zinc-200">
                Message received. We'll get back to you shortly.
              </p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="mt-5 space-y-3">
              <input
                data-testid="contact-input-name"
                className={INPUT}
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                data-testid="contact-input-email"
                type="email"
                className={INPUT}
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <textarea
                data-testid="contact-input-message"
                className={`${INPUT} h-auto min-h-[90px] resize-none py-3`}
                placeholder="How can we help?"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
              {error && (
                <p data-testid="contact-form-error" className="text-sm font-medium text-red-400">{error}</p>
              )}
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="flex items-center gap-2 rounded-full bg-purple-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-purple-500 disabled:opacity-60"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                Send Message
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-5 py-7 text-xs text-zinc-600 sm:flex-row sm:items-center sm:px-8">
          <p>© 2026 Viral Pulse Agency. All rights reserved. Las Vegas, NV.</p>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors hover:text-purple-400">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="transition-colors hover:text-purple-400">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
